import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';
import { Dropdown, DropdownButton, DropdownDivider } from '.';

import type { JSX } from 'react';
import type { IconProp } from 'components/common/icons/Icon';

type ButtonOption<T> = {
  disabled?: boolean;
  icon?: IconProp;
  iconContent?: React.ReactNode;
  subText?: string;
  text: string | JSX.Element;
  type?: 'button' | undefined;
  value: T;
};

type DividerOption = {
  title?: string;
  type: 'divider';
};

interface Props<T> extends React.ComponentProps<typeof Dropdown> {
  defaultText?: string;
  defaultValue: T;
  dropdownTextMinWidth?: string;
  dropdownWhiteSpace?: React.CSSProperties['whiteSpace'];
  isClearable?: boolean;
  onChange: (value: T) => void;
  options: (ButtonOption<T> | DividerOption)[];
  optionStyle?: React.CSSProperties;
  selectedValueBefore?: string;
  showSelectedIcon?: boolean;
  showSelectedText?: boolean;
  titleMaxWidth?: React.CSSProperties['maxWidth'];
  value?: T;
}

const DropdownSelectable = <T extends string | number>({
  buttonStyle,
  defaultText,
  defaultValue,
  dropdownTextMinWidth,
  dropdownWhiteSpace,
  isClearable,
  onChange,
  options,
  optionStyle,
  selectedValueBefore,
  showSelectedIcon,
  showSelectedText,
  titleMaxWidth,
  value,
  ...props
}: Props<T>) => {
  const { messages, theme } = useReduxState();

  const [state, setState] = useState(value || defaultValue);

  useEffect(() => {
    if (value && value !== state) setState(value);
  }, [value]);

  const handleChange = (value: T) => {
    if (value !== state) {
      setState(value);
      if (onChange) onChange(value);
    }
  };

  const buttonOptions = options?.filter(
    (option) => option.type !== 'divider'
  ) as ButtonOption<T>[];

  return (
    <div className="selectable">
      <Dropdown
        before={state && selectedValueBefore ? selectedValueBefore : null}
        buttonStyle={
          state && isClearable
            ? { paddingRight: 36, ...buttonStyle }
            : buttonStyle
        }
        icon={
          showSelectedIcon
            ? buttonOptions?.find((o) => o.value === state)?.icon
            : null
        }
        isMenu={true}
        showArrow={!isClearable || !state}
        styleContentButtons={false}
        title={
          state && showSelectedText ? (
            <span
              style={
                titleMaxWidth
                  ? {
                      display: 'inline-block',
                      overflow: 'hidden',
                      maxWidth: titleMaxWidth,
                      textOverflow: 'ellipsis',
                      verticalAlign: 'bottom'
                    }
                  : null
              }
            >
              {buttonOptions?.find((o) => o.value === state)?.text}
            </span>
          ) : (
            defaultText
          )
        }
        {...props}
      >
        {options && options.length ? (
          options.map((option, i) =>
            option.type === 'divider' ? (
              <DropdownDivider key={option.title || i} title={option.title} />
            ) : (
              <DropdownButton
                key={option.value}
                disabled={option.disabled}
                icon={option.icon}
                iconContent={option.iconContent}
                isSelectable={true}
                isSelected={state === option.value}
                onClick={() => handleChange(option.value)}
                role="menuitemradio"
                style={optionStyle}
                text={option.text}
                subText={option.subText}
                textMinWidth={dropdownTextMinWidth}
                whiteSpace={dropdownWhiteSpace}
              />
            )
          )
        ) : (
          <div className="no-options">{messages.commonNoOptions}</div>
        )}
      </Dropdown>

      {state && isClearable ? (
        <button
          aria-label={messages.commonClear}
          className="clear-button"
          onClick={() => handleChange(defaultValue)}
        >
          <Icon icon="cross" type="custom" />
        </button>
      ) : null}

      <style jsx>{`
        .selectable {
          display: inline-block;
          position: relative;
        }
        .no-options {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-style: italic;
          font-weight: 600;
          padding: 4px 15px;
          white-space: nowrap;
        }
        .clear-button {
          background: transparent;
          border: 0;
          border-radius: 4px;
          color: ${theme.textColorLighter};
          cursor: pointer;
          font-size: 1.6rem;
          line-height: 0;
          margin-left: 5px;
          outline: 0;
          padding: 5px 5px 4px;
          position: absolute;
          right: 5px;
          top: 5px;
          transition: background-color 0.3s;
        }
        .clear-button:focus,
        .clear-button:hover {
          background-color: ${theme.lightGrey};
        }
      `}</style>
    </div>
  );
};

export default DropdownSelectable;
